import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
} from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { BookMeetingService } from '../../../../../core/services/book-meeting.service';
import { LocaleService } from '../../../../../core/services/locale.service';
import { PaperIconComponent } from '@ricoh-paper/angular';

/**
 * ConferencingSectionComponent — Microsoft Teams conferencing toggle row.
 *
 * RESPONSIBILITY: Displays a styled on/off toggle for adding an online
 * meeting link. Visual state changes between active (indigo tint) and
 * inactive (neutral). Description text adapts based on whether remote
 * attendees are present.
 *
 * SIGNALS:
 *   isOnlineMeeting (from BookMeetingService) — read/write toggle state
 *
 * USED BY: BookMeetingPanelComponent (form view, between People and More Options)
 */
@Component({
  selector: 'im-conferencing-section',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [PaperIconComponent],
  template: `
    @if (!readonly() || service.isOnlineMeeting()) {
    <!-- Mirrors the Flow row's clean toggle layout: a <label> wrapping
         the toggle + copy so the entire row is one clickable hit-area.
         No bespoke pill chrome — the section icon in the panel's
         v2-gutter does the visual anchoring, and the toggle does
         everything else. -->
    <label class="bmp__conf-row">
      <button
        type="button"
        class="bmp__conf-toggle"
        [class.is-on]="service.isOnlineMeeting()"
        role="switch"
        [attr.aria-checked]="service.isOnlineMeeting()"
        [attr.aria-label]="toggleAria()"
        [disabled]="readonly()"
        (click)="onToggleClick()">
        <span class="bmp__conf-toggle-track">
          <span class="bmp__conf-toggle-thumb"></span>
        </span>
      </button>
      <div class="bmp__conf-copy">
        <span class="bmp__conf-title">{{ providerTitle() }}</span>
        <span class="bmp__conf-sub">{{ description() }}</span>
      </div>
    </label>
    }
  `,
  styles: [`
    :host { display: block; }

    .bmp__conf-row {
      display: flex;
      align-items: center;
      gap: 12px;
      cursor: pointer;
    }

    .bmp__conf-toggle {
      border: none;
      background: transparent;
      padding: 0;
      cursor: pointer;
      flex-shrink: 0;
      &:disabled { cursor: default; }
      &:focus-visible {
        outline: 2px solid var(--brand-focus-ring, var(--color-primary));
        outline-offset: 2px;
        border-radius: 999px;
      }
    }

    .bmp__conf-toggle-track {
      display: flex;
      align-items: center;
      width: 36px;
      height: 20px;
      border-radius: 999px;
      background: var(--border-med, var(--divider));
      padding: 2px;
      transition: background var(--motion-fast, 200ms) var(--ease, ease);
    }

    .bmp__conf-toggle.is-on .bmp__conf-toggle-track {
      background: var(--color-primary);
    }

    .bmp__conf-toggle-thumb {
      display: block;
      width: 16px;
      height: 16px;
      border-radius: 50%;
      background: var(--surface-raised, white);
      box-shadow: 0 1px 2px color-mix(in srgb, var(--text) 14%, transparent);
      transition: transform var(--motion-fast, 200ms) var(--ease, ease);
    }

    .bmp__conf-toggle.is-on .bmp__conf-toggle-thumb {
      transform: translateX(16px);
    }

    .bmp__conf-copy {
      display: flex;
      flex-direction: column;
    }
    .bmp__conf-title {
      font-size: 13px;
      font-weight: 500;
      color: var(--text);
    }
    .bmp__conf-sub {
      font-size: 11.5px;
      color: var(--text-tertiary);
    }

    @media (prefers-reduced-motion: reduce) {
      .bmp__conf-toggle-track,
      .bmp__conf-toggle-thumb { transition: none; }
    }
  `],
})
export class ConferencingSectionComponent {
  protected readonly service = inject(BookMeetingService);
  private readonly transloco = inject(TranslocoService);
  private readonly locale = inject(LocaleService);

  /** Whether any remote (non-in-person) attendees are in the meeting */
  readonly hasRemote = input(false);

  /** Driven by BookMeetingService.mode — replaces the toggle with a
   *  static "Online meeting" / "In-person only" tag in view mode. */
  protected readonly readonly = computed(() => this.service.mode() === 'view');

  /** Click handler — a no-op in readonly mode (the button is also
   *  disabled, so this is belt-and-braces). Flips isOnlineMeeting
   *  via the service signal. */
  protected onToggleClick(): void {
    if (this.readonly()) return;
    this.service.isOnlineMeeting.set(!this.service.isOnlineMeeting());
  }

  protected readonly description = computed(() => {
    this.locale.locale();
    if (!this.service.isOnlineMeeting()) {
      return this.transloco.translate('viewer.book_meeting_panel.conferencing.in_person_only');
    }
    return this.hasRemote()
      ? this.transloco.translate('viewer.book_meeting_panel.conferencing.hybrid_link')
      : this.transloco.translate('viewer.book_meeting_panel.conferencing.meeting_link_in_invite');
  });

  protected readonly toggleAria = computed(() => {
    return this.transloco.translate('viewer.book_meeting_panel.conferencing.toggle_aria', {
      provider: this.providerTitle(),
    });
  });

  /** Resolves the conferencing provider title from the primary room's
   *  calendar wiring. Microsoft Graph rooms get "Microsoft Teams",
   *  Google rooms get "Google Meet", anything else (Zoom integrations,
   *  generic ICS, etc.) falls back to a neutral "Video conferencing"
   *  so the UI doesn't lie about which provider will mint the link. */
  protected readonly providerTitle = computed<string>(() => {
    this.locale.locale();
    const room = this.service.rooms().find(r => r.isPrimary) ?? this.service.rooms()[0];
    const provider = (room?.calendarProvider ?? '').toLowerCase();
    if (provider.includes('microsoft') || provider.includes('teams') || provider === 'graph') {
      return 'Microsoft Teams';
    }
    if (provider.includes('google') || provider.includes('meet') || provider === 'gcal') {
      return 'Google Meet';
    }
    if (provider.includes('zoom')) {
      return 'Zoom';
    }
    return this.transloco.translate('viewer.book_meeting_panel.conferencing.video_conferencing');
  });
}
